import { HStack, PageContainer, Text, VStack } from '@/components/Atoms'
import { Block } from '@/components/Block'
import { CrtImportHistoryButtons } from '@/components/CrtImportHistoryButtons'
import { CrtImportPeriodNotFound } from '@/components/CrtImportPeriodNotFound'
import { DetailsHeader } from '@/components/DetailsHeader'
import { ExportCsvMenu, ExportCsvMenuItem } from '@/components/ExportCsv/ExportCsvMenu'
import PermissionsGuard from '@/components/Guard/PermissionsGuard'
import { PendingChanges } from '@/components/PendingChanges'
import { Spinner } from '@/components/Spinner'
import {
  currentChannelContributionsQueryOptions,
  channelContributionsDetailsExportQueryOptions,
  channelContributionsDetailsRawExportQueryOptions,
  channelContributionsDetailsSearchSchema,
  useChannelContributionsById,
  useUpdateChannelContributions,
} from '@/hooks/queries/crt/useChannelContributions'
import { useLastUpdatedChannelContributionsLog } from '@/hooks/queries/useAuditTrails'
import { useLocalizedDate } from '@/hooks/useLocalizedDate'
import { useTablePagination } from '@/hooks/useTablePagination'
import { formatLastUpdatedAssumptionLog } from '@/lib/utils/crt'
import { crtPeriodFromParams } from '@/lib/utils/crtPeriodParams'
import { queryClient } from '@/main'
import { parseDateOnly } from '@/utils/DateOnlyHelpers'
import { UpdateChannelContributions } from '@repo/backend/crt'
import { createFileRoute } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import ChannelContributionsDetailsDataTable from './-components/ChannelContributionsDetails/ChannelContributionsDetailsDataTable'

export const Route = createFileRoute(
  '/_auth/_modules/crt/volume-forecasts/channel-contributions/$year/$month',
)({
  component: ChannelContributionsPeriod,
  validateSearch: (search) => channelContributionsDetailsSearchSchema.parse(search),
  loader: async ({ params }) => {
    const crtPeriod = crtPeriodFromParams(params)
    const channelContributions = crtPeriod
      ? await queryClient.fetchQuery(currentChannelContributionsQueryOptions(crtPeriod))
      : null

    return {
      crumb: channelContributions?.name ?? `${params.year}/${params.month}`,
      crtPeriod,
      channelContributionId: channelContributions ? String(channelContributions.id) : null,
    }
  },
})

function ChannelContributionsPeriod() {
  const { formatMonthYear } = useLocalizedDate()
  const { year, month } = Route.useParams()
  const { crtPeriod, channelContributionId } = Route.useLoaderData()

  if (!channelContributionId) {
    return (
      <CrtImportPeriodNotFound
        period={crtPeriod ? formatMonthYear(parseDateOnly(crtPeriod)) : `${year}/${month}`}
        otherPeriodsTo="/crt/volume-forecasts/channel-contributions"
      />
    )
  }

  return (
    <ChannelContributionsDetails
      key={channelContributionId}
      channelContributionId={channelContributionId}
    />
  )
}

function ChannelContributionsDetails({ channelContributionId }: { channelContributionId: string }) {
  const { t } = useTranslation(['crt', 'common'])
  const { formatMonthYear } = useLocalizedDate()
  const navigate = Route.useNavigate()
  const search = Route.useSearch()

  const { pageSize, onPageSizeChange } = useTablePagination({ navigate, search })

  const { month: _month, year: _year, ...apiSearch } = search
  const { data, isPending, isError } = useChannelContributionsById(channelContributionId, {
    ...apiSearch,
    page: apiSearch.page ?? 1,
    pageSize,
  })
  const { data: lastUpdatedChannelContributionsTrail } =
    useLastUpdatedChannelContributionsLog(channelContributionId)

  const [formState, setFormState] = useState({
    name: '',
    description: '',
  })

  useEffect(() => {
    if (data) {
      setFormState({
        name: data.name,
        description: data.description ?? '',
      })
    }
  }, [data])

  const getUnsavedChanges = (): UpdateChannelContributions | null => {
    if (!data) return null

    const changes: UpdateChannelContributions = {}
    let hasChanges = false

    if (formState.name !== data.name) {
      changes.name = formState.name
      hasChanges = true
    }

    if (formState.description !== (data.description ?? '')) {
      changes.description = formState.description
      hasChanges = true
    }

    return hasChanges ? changes : null
  }

  const hasUnsavedChanges = getUnsavedChanges() !== null

  const lastUpdatedLog = formatLastUpdatedAssumptionLog(lastUpdatedChannelContributionsTrail)

  const { mutate: updateChannelContributions, isPending: isUpdatePending } =
    useUpdateChannelContributions({
      channelContributionId: Number(channelContributionId),
    })

  const handleUpdateName = (name: string) => {
    setFormState((prev) => ({ ...prev, name }))
  }

  const handleUpdateDescription = (description: string) => {
    setFormState((prev) => ({ ...prev, description }))
  }

  const handleSave = () => {
    const unsavedChanges = getUnsavedChanges()
    if (!unsavedChanges) return

    updateChannelContributions(unsavedChanges)
  }

  if (isPending) {
    return (
      <PageContainer className="flex items-center justify-center">
        <Spinner size="lg" />
      </PageContainer>
    )
  }

  if (isError || !data) {
    return (
      <PageContainer>
        <Text>{t('errors.channelContributions.fetchFailed')}</Text>
      </PageContainer>
    )
  }

  return (
    <PageContainer className="pb-28">
      <Block shouldBlock={hasUnsavedChanges} />

      <HStack gap="sm" className="justify-between">
        <DetailsHeader
          name={formState.name}
          description={formState.description}
          onUpdateName={handleUpdateName}
          onUpdateDescription={handleUpdateDescription}
          permission="crt_volume_forecasts_configure"
        />

        <VStack gap="md" className="items-end">
          <Text className="text-muted-foreground text-sm">
            {t('crtPeriod', { ns: 'crt' })}: {formatMonthYear(parseDateOnly(data.crtPeriod))}
          </Text>
          <HStack gap="sm">
            <ExportCsvMenu
              items={[
                <ExportCsvMenuItem
                  label={t('export.viewData', { ns: 'common' })}
                  description={t('export.viewDataDescription', { ns: 'common' })}
                  getCsvExport={() =>
                    queryClient.ensureQueryData(
                      channelContributionsDetailsExportQueryOptions(
                        channelContributionId,
                        apiSearch,
                      ),
                    )
                  }
                  rowCount={data.detailsRowCount}
                />,
                <ExportCsvMenuItem
                  label={t('export.rawData', { ns: 'common' })}
                  description={t('export.rawDataDescription', { ns: 'common' })}
                  getCsvExport={() =>
                    queryClient.ensureQueryData(
                      channelContributionsDetailsRawExportQueryOptions(channelContributionId),
                    )
                  }
                  rowCount={data.detailsRowCount}
                  canExceedRowCountLimit
                />,
              ]}
            />
            <CrtImportHistoryButtons
              otherPeriodsTo="/crt/volume-forecasts/channel-contributions"
              uploadType="import-channel-contributions-details"
              uploadAttributes={{ 'channel-contribution-id': channelContributionId }}
            />
          </HStack>
        </VStack>
      </HStack>

      <ChannelContributionsDetailsDataTable
        channelContributionId={channelContributionId}
        channelContributions={data}
        pageSize={pageSize}
        onPageSizeChange={onPageSizeChange}
      />

      <PermissionsGuard required={['crt_volume_forecasts_configure']}>
        <PendingChanges
          areChangesPending={hasUnsavedChanges}
          isSavePending={isUpdatePending}
          lastUpdatedLog={lastUpdatedLog}
          onSaveChanges={handleSave}
        />
      </PermissionsGuard>
    </PageContainer>
  )
}
